import { type FC, type RefObject, useEffect, useMemo, useState } from "react";

import { type DraftParagraphLockEnum } from "@app_schema";

import { ErrorLine } from "@styled/error_line";
import { FloatingTip } from "@styled/floating_tip";
import { ParagraphEditor } from "@styled/paragraph_editor";

import {
  type EditTarget,
  spotIn,
  targetElement,
  useDraftBlockClicks,
} from "./use_draft_block_clicks";
import {
  type DraftParagraphEdit,
  type EditSpot,
} from "./use_draft_paragraph_edit";

const keyOf = (target: EditTarget) =>
  "block" in target
    ? String(target.block)
    : `${String(target.marker)}:${String(target.line)}`;

// Live editing over the drawn Report: the editor on the paragraph or drafted
// line he clicked, kept on it as the Report scrolls and saved when he leaves
// it, or a tip on why a part stays as it is in his Word file.
export const DraftEditLayer: FC<{
  edit: DraftParagraphEdit;
  // The blocks that stay as they are in his file.
  locks: ReadonlyArray<{ block: number; lock: DraftParagraphLockEnum }>;
  frameRef: RefObject<HTMLIFrameElement | null>;
  root: Element | null;
  drawn: unknown;
  enabled: boolean;
  writing: number | null;
}> = ({ edit, locks, frameRef, root, drawn, enabled, writing }) => {
  const [spot, setSpot] = useState<EditSpot | null>(null);
  const target = edit.editing;
  const key = target ? keyOf(target) : undefined;
  const report = frameRef.current?.contentDocument;
  const outside = useMemo(
    () => (report ? [document, report] : [document]),
    [report],
  );

  useDraftBlockClicks({
    frameRef,
    root,
    drawn,
    enabled: enabled && !target,
    locks,
    writing,
    onEdit: edit.open,
    onTip: edit.setTip,
  });

  useEffect(() => {
    const frame = frameRef.current;
    const view = frame?.contentWindow;
    if (!target || !frame || !root || !view) {
      setSpot(null);
      return;
    }
    // Found once; a scroll only measures it.
    const element = targetElement(root, target);
    const place = () => setSpot(element && spotIn(frame, element));
    place();
    let pending = 0;
    const scroll = () => {
      if (pending) return;
      pending = view.requestAnimationFrame(() => {
        pending = 0;
        place();
      });
    };
    view.addEventListener("scroll", scroll, { passive: true });
    return () => {
      view.cancelAnimationFrame(pending);
      view.removeEventListener("scroll", scroll);
    };
  }, [target, frameRef, root, drawn]);

  return (
    <>
      {edit.tip && !target && (
        <FloatingTip top={edit.tip.top} left={edit.tip.left}>
          {edit.tip.text}
        </FloatingTip>
      )}
      {edit.editing && spot && edit.runs && (
        <ParagraphEditor
          key={key}
          spot={spot}
          look={edit.editing.look}
          caret={edit.editing.caret}
          runs={edit.runs}
          saving={edit.saving}
          active={enabled}
          outside={outside}
          line={edit.error && <ErrorLine>{edit.error}</ErrorLine>}
          onDone={(runs) => void edit.done(runs)}
          onCancel={edit.close}
        />
      )}
    </>
  );
};
